import { angleDelta } from '../game/math'
import type { SimulationState } from '../game/types'

export const PREMIUM_CAR_MODEL_URL = '/models/lambo.glb'
export const PREMIUM_CAR_MODEL_YAW = 0
export const WINDOW_GUNNER_MODEL_URL = '/models/tride-window-gunner.glb'
export const CAR_RIDE_HEIGHT = 0.64
export const CAR_WHEEL_RADIUS = 0.58
export const WHEEL_RIG = [
  { name: 'RR', direction: -1 },
  { name: 'RL', direction: 1 },
  { name: 'FR', direction: -1 },
  { name: 'FL', direction: 1 },
] as const

export function getWheelSpinDelta(speed: number, delta: number, direction: number): number {
  return (speed / CAR_WHEEL_RADIUS) * Math.max(0, Math.min(delta, 0.05)) * direction
}

export function dampVisualAngle(current: number, target: number, delta: number): number {
  return current + angleDelta(current, target) * (1 - Math.exp(-Math.max(0, delta) * 14))
}

export function isVisualTeleport(current: { x: number; y: number }, target: { x: number; y: number }): boolean {
  return (target.x - current.x) ** 2 + (target.y - current.y) ** 2 > 64
}

interface CanvasProbe {
  getContext(type: 'webgl2' | 'webgl'): unknown
}

export function createWebGLDetector(createCanvas: () => CanvasProbe, getUserAgent: () => string): () => boolean {
  let supported: boolean | undefined
  return () => {
    if (supported !== undefined) return supported
    try {
      if (getUserAgent().toLowerCase().includes('jsdom')) {
        supported = false
        return supported
      }
      const canvas = createCanvas()
      supported = Boolean(canvas.getContext('webgl2') || canvas.getContext('webgl'))
    } catch {
      supported = false
    }
    return supported
  }
}

export interface LightformerDefinition {
  kind: 'ceiling' | 'side' | 'key'
  position: [number, number, number]
  rotation: [number, number, number]
  scale: [number, number, number]
  intensity: number
  color: string
}

export const LIGHTFORMER_RIG: readonly LightformerDefinition[] = [
  ...[-72, -48, -24, 0, 24, 48, 72].map(
    (z): LightformerDefinition => ({
      kind: 'ceiling',
      position: [0, 34, z],
      rotation: [Math.PI / 2, 0, 0],
      scale: [80, 3, 1],
      intensity: 2.4,
      color: '#c8f7ff',
    }),
  ),
  {
    kind: 'side',
    position: [-110, 12, 0],
    rotation: [0, Math.PI / 2, 0],
    scale: [180, 10, 1],
    intensity: 1.8,
    color: '#286cff',
  },
  {
    kind: 'side',
    position: [110, 12, 0],
    rotation: [0, -Math.PI / 2, 0],
    scale: [180, 10, 1],
    intensity: 1.6,
    color: '#5ef2ff',
  },
  {
    kind: 'key',
    position: [46, 24, 44],
    rotation: [0, -0.8, 0],
    scale: [15, 15, 1],
    intensity: 8,
    color: '#ff315f',
  },
]

export interface CameraTuning {
  distance: number
  height: number
  fov: number
  rollScale: number
}

export function getCameraTuning(speed: number, reducedMotion: boolean): CameraTuning {
  const normalized = Math.min(1, Math.max(0, Math.abs(speed) / 72))
  return {
    distance: 8.8 + normalized * 3.5,
    height: 4.2 + normalized * 0.75,
    fov: reducedMotion ? 60 : 60 + normalized * 9,
    rollScale: reducedMotion ? 0 : 0.032,
  }
}

export function getSceneCarDescriptors(state: SimulationState) {
  return state.vehicles.map((vehicle, index) => ({
    vehicleId: vehicle.id,
    index,
    modelUrl: PREMIUM_CAR_MODEL_URL,
    gunnerModelUrl: WINDOW_GUNNER_MODEL_URL,
    nameplateId: vehicle.id,
  }))
}
